import { validateSync } from 'class-validator';
import {
  registerDecorator,
  ValidationArguments,
  ValidationOptions,
  ValidatorConstraint,
  ValidatorConstraintInterface,
} from 'class-validator';
import { action, makeObservable, observable } from 'mobx';

export function IsFullname(validationOptions?: ValidationOptions) {
  return function (object: any, propertyName: string) {
    registerDecorator({
      name: 'IsFullname',
      target: object.constructor,
      propertyName,
      options: validationOptions,
      validator: IsFullnameConstraint,
    });
  };
}

export function Match(propertyGetter: (arg: any) => any, validationOptions?: ValidationOptions) {
  return (object: any, propertyName: string) => {
    registerDecorator({
      target: object.constructor,
      propertyName,
      options: validationOptions,
      constraints: [propertyGetter],
      validator: MatchConstraint,
    });
  };
}

@ValidatorConstraint({ name: 'IsFullname' })
export class IsFullnameConstraint implements ValidatorConstraintInterface {
  validate(value: any, args: ValidationArguments) {
    if (!value) {
      return false;
    }

    value = value.trim();

    const regex = /^[a-zA-ZÀ-ÿ]+(([',. -][a-zA-ZÀ-ÿ ])?[a-zA-ZÀ-ÿ]*)*$/;

    if (!regex.test(value)) {
      return false;
    }

    if (value.split(' ').length >= 2) {
      return true;
    }
    return false;
  }

  defaultMessage(args: ValidationArguments) {
    return 'Nome inválido. Digite seu nome completo.';
  }
}

@ValidatorConstraint({ name: 'Match' })
export class MatchConstraint implements ValidatorConstraintInterface {
  validate(value: any, args: ValidationArguments) {
    const [propertyGetter] = args.constraints;
    const relatedValue = propertyGetter(args.object as any);
    return value === relatedValue;
  }
}

@ValidatorConstraint({ name: 'isStrongPassword', async: false })
class IsStrongPasswordConstraint implements ValidatorConstraintInterface {
  validate(password: string, args: ValidationArguments) {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
    return regex.test(password);
  }

  defaultMessage(args: ValidationArguments) {
    return 'A senha deve conter pelo menos 6 caracteres, uma letra maiúscula, uma letra minúscula e um número.';
  }
}

export function IsStrongPassword() {
  return function (object: any, propertyName: string) {
    registerDecorator({
      name: 'isStrongPassword',
      target: object.constructor,
      propertyName: propertyName,
      options: {},
      validator: IsStrongPasswordConstraint,
    });
  };
}

export class ObservableForm<T extends object> {
  private form: T;
  private originalForm: T;
  hasError: boolean;
  isDirty: boolean;
  isValid: boolean;
  private formConstructor: new () => T;

  constructor(Class: new () => T) {
    this.formConstructor = Class;
    this.originalForm = new Class();

    this.form = new Class();
    this.values = { ...this.form };

    this.isValid = !(validateSync(this.form).length > 0);
    makeObservable(this, {
      values: observable,
      errors: observable,
      setValue: action,
      setValueAndValidateValue: action,
      validateField: action,
      validate: action,
      updateState: action,
      reset: action,
      isDirty: observable,
      isValid: observable,
      hasError: observable,
    });
    this.softValidate();
  }

  values: Record<keyof T, any> = {} as Record<keyof T, any>;
  errors: Record<keyof T, string[]> = {} as Record<keyof T, string[]>;

  setValue(fieldName: keyof T, value: any) {
    this.form[fieldName] = value;
    this.values = { ...this.values, [fieldName]: value };
    this.softValidate();
  }

  setValueAndValidateValue(fieldName: keyof T, value: any) {
    this.setValue(fieldName, value);
    this.validateField(fieldName);
  }

  validateField(fieldName: keyof T) {
    this.errors = { ...this.errors, [fieldName]: [] };

    const fieldErrors = validateSync(this.form, {
      validationError: { target: false },
    });

    for (const error of fieldErrors) {
      if (error.property !== fieldName) {
        continue;
      }

      Object.keys(error.constraints).forEach((key) => {
        this.errors[fieldName] = this.errors[fieldName] || [];
        this.errors = {
          ...this.errors,
          [fieldName]: [...this.errors[fieldName], error.constraints[key]],
        };
      });
    }

    this.updateState();
  }

  validate() {
    const fieldErrors = validateSync(this.form, {
      validationError: { target: false },
    });

    for (const error of fieldErrors) {
      Object.keys(error.constraints).forEach((key) => {
        this.errors[error.property] = this.errors[error.property] || [];
        this.errors = {
          ...this.errors,
          [error.property]: [...this.errors[error.property], error.constraints[key]],
        };
      });
    }

    this.updateState();
  }

  softValidate() {
    this.isValid = !(validateSync(this.form).length > 0);
  }

  reset() {
    this.form = new this.formConstructor();
    this.values = { ...this.form };
    this.errors = {} as Record<keyof T, string[]>;
    this.softValidate();
  }

  updateState() {
    this.hasError = Object.keys(this.errors).some((key) => this.errors[key].length > 0);
    this.isDirty = JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
    this.isValid = !(validateSync(this.form).length > 0);
  }
}
